import { z } from 'zod';
import { getPhotoSchema, photoOptionalSchema, USAAddressOptionalSchema, USAAddressSchema } from '../../../constants/sharedSchemas';

const adultsSchema = z.array(
  z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    dateOfBirth: z.date({ required_error: 'Date of birth is required' }),
    gender: z.string().min(1, 'Gender is required'),
    relationship: z.string().min(1, 'Relationship is required'),
    isThisHostParent: z.string().min(1, 'Please specify'),
    interestsActivities: z.array(z.object({ value: z.string() })).min(1, 'Interests is required'),
    nativeLanguage: z.string().min(1, 'Please specify your language'),
    educationLevel: z.string().min(1, 'Education level is required'),
    communityInvolvement: z.string().min(1, 'Community Involvement is required'),
    livingAtHome: z.string().min(1, 'Please specify'),
    partTimeExplanation: z.string().min(1, 'Please explain'),
    cellPhoneNumber: z.string().min(1, 'Cell Phone Number is required'),
    workPhoneNumber: z.string().min(1, 'Work Phone Number is required'),
    emailAddress: z.string().min(1, 'Email address is required').email('Email address is invalid'),
    socialMediaProfileLink: z.string().min(1, 'Social media profile link is required').url('URL address is invalid'),
    employer: z.string().min(1, 'Employer is required'),
    jobTitle: z.string().min(1, 'Job title is required'),
    workSchedule: z.string().min(1, 'Work schedule is required'),
    jobContactName: z.string().min(1, 'Job contact name is required'),
    jobContactPhoneNumber: z.string().min(1, 'Job contact phone number is required'),
    photo: photoOptionalSchema,
  }),
);

const adultsOptionalSchema = z.array(
  z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    dateOfBirth: z.date().optional(),
    gender: z.string().optional(),
    relationship: z.string().optional(),
    isThisHostParent: z.string().optional(),
    interestsActivities: z.array(z.object({ value: z.string() })).optional(),
    nativeLanguage: z.string().optional(),
    educationLevel: z.string().optional(),
    communityInvolvement: z.string().optional(),
    livingAtHome: z.string().optional(),
    partTimeExplanation: z.string().optional(),
    cellPhoneNumber: z.string().optional(),
    workPhoneNumber: z.string().optional(),
    emailAddress: z.union([z.literal(''), z.string().email('Email is invalid').optional()]),
    socialMediaProfileLink: z.union([z.literal(''), z.string().url('URL address is invalid').optional()]),
    employer: z.string().optional(),
    jobTitle: z.string().optional(),
    workSchedule: z.string().optional(),
    jobContactName: z.string().optional(),
    jobContactPhoneNumber: z.string().optional(),
    photo: photoOptionalSchema,
  }),
);

// Schema for when singleAdultConfirm is false (fields are required)
const hasAdultsSchema = z.object({
  singleAdultConfirm: z.literal(false),
  adults: adultsSchema,
});

// Schema for when singleAdultConfirm is true (fields are optional)
const noAdultsSchema = z.object({
  singleAdultConfirm: z.literal(true),
  adults: adultsOptionalSchema,
});

const adultsSectionSchema = z.discriminatedUnion('singleAdultConfirm', [hasAdultsSchema, noAdultsSchema]);

const childrenSchema = z.array(
  z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    dateOfBirth: z.date({ required_error: 'Date of birth is required' }),
    gender: z.string().min(1, 'Gender is required'),
    expectedGraduationDate: z.string().min(1, 'Expected graduation date is required'),
    communityInvolvement: z.string().min(1, 'Community involvement is required'),
    livingAtHome: z.string().min(1, 'Please specify'),
    partTimeExplanation: z.string().min(1, 'Please explain'),
    interestsActivities: z.array(z.object({ value: z.string() })).min(1, 'Interests is required'),
    photo: photoOptionalSchema,
  }),
);

const childrenOptionalSchema = z.array(
  z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    dateOfBirth: z.date().optional(),
    gender: z.string().optional(),
    expectedGraduationDate: z.string().optional(),
    communityInvolvement: z.string().optional(),
    livingAtHome: z.string().optional(),
    partTimeExplanation: z.string().optional(),
    interestsActivities: z.array(z.object({ value: z.string() })).optional(),
    photo: photoOptionalSchema,
  }),
);

// Schema for when noChildrenAtHome is false (fields are required)
const hasChildrenSchema = z.object({
  noChildrenAtHome: z.literal(false),
  childrenList: childrenSchema,
});

// Schema for when noChildrenAtHome is true (fields are optional)
const noChildrenSchema = z.object({
  noChildrenAtHome: z.literal(true),
  childrenList: childrenOptionalSchema,
});

const childrenSectionSchema = z.discriminatedUnion('noChildrenAtHome', [hasChildrenSchema, noChildrenSchema]);

const mailingAddressSchema = z.discriminatedUnion('isMailingSame', [
  z.object({
    isMailingSame: z.literal(false),
    mailingAddress: USAAddressSchema,
  }),
  z.object({
    isMailingSame: z.literal(true),
    mailingAddress: USAAddressOptionalSchema,
  }),
]);

export const familyOverviewSchema = {
  required: z
    .object({
      overview: z
        .object({
          languageInHousehold: z.string().min(1, 'Language is required'),
        })
        .and(adultsSectionSchema),
      children: childrenSectionSchema,
      contactInformation: z
        .object({
          physicalAddress: USAAddressSchema,
          nearestAirport: z.string().min(1, 'Nearest airport is required'),
          emergencyContactPerson: z.string().min(1, 'Emergency contact person is required'),
          emergencyPhoneNumber: z.string().min(1, 'Emergency contact number is required'),
        })
        .and(mailingAddressSchema),
      familyPhoto: getPhotoSchema('Family photo'),
    })
    .and(childrenSectionSchema),
  optional: z.object({
    overview: z.object({
      languageInHousehold: z.string().optional(),
      singleAdultConfirm: z.boolean(),
      adults: adultsOptionalSchema,
    }),
    children: z.object({
      noChildrenAtHome: z.boolean(),
      childrenList: childrenOptionalSchema,
    }),
    contactInformation: z.object({
      physicalAddress: USAAddressOptionalSchema,
      isMailingSame: z.boolean(),
      mailingAddress: USAAddressOptionalSchema,
      nearestAirport: z.string().optional(),
      emergencyContactPerson: z.string().optional(),
      emergencyPhoneNumber: z.string().optional(),
    }),
    familyPhoto: photoOptionalSchema,
  }),
};

export type FamilyOverviewFormValues = z.infer<(typeof familyOverviewSchema)['required']> | z.infer<(typeof familyOverviewSchema)['optional']>;
type Adult = FamilyOverviewFormValues['overview']['adults'][number];
type Child = FamilyOverviewFormValues['children']['childrenList'][number];

export const adultDefaultValues: Adult = {
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  gender: '',
  relationship: '',
  isThisHostParent: '',
  interestsActivities: [],
  nativeLanguage: '',
  educationLevel: '',
  communityInvolvement: '',
  livingAtHome: '',
  partTimeExplanation: '',
  cellPhoneNumber: '',
  workPhoneNumber: '',
  emailAddress: '',
  socialMediaProfileLink: '',
  employer: '',
  jobTitle: '',
  workSchedule: '',
  jobContactName: '',
  jobContactPhoneNumber: '',
  photo: { id: '' },
};

const childrenDefaultValues: Child = {
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  gender: '',
  expectedGraduationDate: '',
  communityInvolvement: '',
  livingAtHome: '',
  partTimeExplanation: '',
  interestsActivities: [],
  photo: { id: '' },
};

export const defaultValues: FamilyOverviewFormValues = {
  // Adults
  overview: {
    languageInHousehold: '',
    singleAdultConfirm: false,
    adults: [adultDefaultValues],
  },
  // Children
  children: {
    noChildrenAtHome: false,
    childrenList: [childrenDefaultValues],
  },
  // Contact Information
  contactInformation: {
    physicalAddress: undefined,
    isMailingSame: false,
    nearestAirport: '',
    emergencyContactPerson: '',
    emergencyPhoneNumber: '',
  },
  // // Family Photo
  familyPhoto: { id: '' },
};
